import { Message } from "shared/components/Message";
import { Spinner } from "components/shared/Spinner";
import classNames from "classnames";
import React, { FC, useEffect, useRef, useState } from "react";
import { MessageDescriptor, useIntl } from "react-intl";
import Button, { ButtonProps } from "reactstrap/lib/Button";
import styles from "./SpinnerButton.module.scss";
import { mapMessages } from "shared/mapMessages";

const messages = mapMessages("components.shared.spinnerbutton", {
    loadingLabel: "Loading",
    stillLoadingLabel: "Still loading",
});

interface IProps {
    isLoading: boolean;
    message?: MessageDescriptor;
}

type Props = IProps & ButtonProps;

export const SpinnerButton: FC<Props> = ({
    isLoading,
    message,
    disabled,
    children,
    "aria-valuemax": ariaValueMax,
    "aria-valuemin": ariaValueMin,
    "aria-valuenow": ariaValueNow,
    "aria-valuetext": ariaValueText,
    ...rest
}) => {
    const { formatMessage } = useIntl();
    const [loadingMessage, setLoadingMessage] = useState<string>("");
    const intervalRef = useRef(0);

    useEffect(() => {
        if (isLoading) {
            if (intervalRef.current) return;
            setLoadingMessage("Loading");
            intervalRef.current = window.setInterval(() => {
                setLoadingMessage(prevMessage => prevMessage === formatMessage(messages.loadingLabel) ? formatMessage(messages.stillLoadingLabel) : formatMessage(messages.loadingLabel));
            }, 5000);
        }
        else {
            clearInterval(intervalRef.current);
            intervalRef.current = 0;
            setLoadingMessage("");
        }
    }, [isLoading, intervalRef, setLoadingMessage]);

    return (
        <Button
            disabled={isLoading || disabled}
            aria-label={message && formatMessage(message)}
            {...rest}
        >
            <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
                {loadingMessage}
            </div>
            <div className={styles.layerContainer}>
                {
                    isLoading &&
                    <div
                        className={styles.layer}
                        role="progressbar"
                        aria-valuemax={ariaValueMax}
                        aria-valuemin={ariaValueMin}
                        aria-valuenow={ariaValueNow}
                        aria-valuetext={ariaValueText}
                    >
                        <Spinner className="icon-button" />
                    </div>
                }
                <div className={styles.layer}>
                    <span className={classNames({ invisible: isLoading })}>
                        {
                            message &&
                            <Message message={message} />
                        }
                        {children}
                    </span>
                </div>
            </div>
        </Button>
    );
};
